import React, { useEffect, useMemo, useState, forwardRef, useImperativeHandle, useRef } from "react";
import { EonUiRadioButtonGroup, EonUiRadioButton, EonUiCheckbox, EonUiButton, EonUiTooltip } from "@eon-ui/eon-ui-components-react";
import { Icon } from "@me8eon/icons";
import "./SourceSelector.scss";
import { useAiWithRag } from "me-at-eon/src/components/ai/contexts/ai.rag.context";
import { ChosenChatbotSource } from "me-at-eon/src/components/ai/hooks/useChatbotIndices.hook";
import { useTranslation } from "@me8eon/translation";
import { SimpleOutsideClick } from "@me8eon/outside_click";

/**
 * Calculate Levenshtein distance between two strings
 */
const levenshteinDistance = (str1: string, str2: string): number => {
    const m = str1.length;
    const n = str2.length;
    const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));

    for (let i = 0; i <= m; i++) dp[i][0] = i;
    for (let j = 0; j <= n; j++) dp[0][j] = j;

    for (let i = 1; i <= m; i++) {
        for (let j = 1; j <= n; j++) {
            if (str1[i - 1] === str2[j - 1]) {
                dp[i][j] = dp[i - 1][j - 1];
            } else {
                dp[i][j] = 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
            }
        }
    }
    return dp[m][n];
};

/**
 * Check if a single query word fuzzy matches a single target word
 */
const fuzzyWordMatch = (targetWord: string, queryWord: string): boolean => {
    if (!queryWord || !targetWord) return false;

    // Exact match or substring match
    if (targetWord.includes(queryWord)) return true;

    // For very short queries, require exact match
    if (queryWord.length < 3) return targetWord === queryWord;

    // Allow ~30% character errors (e.g., 2 errors for 7 chars)
    const threshold = Math.max(1, Math.floor(queryWord.length * 0.3));
    const distance = levenshteinDistance(targetWord, queryWord);
    if (distance <= threshold) return true;

    // Check if query is a fuzzy substring of longer words
    if (targetWord.length > queryWord.length) {
        for (let i = 0; i <= targetWord.length - queryWord.length; i++) {
            const substring = targetWord.substring(i, i + queryWord.length);
            if (levenshteinDistance(substring, queryWord) <= threshold) return true;
        }
    }

    return false;
};

/**
 * Check if query fuzzy matches target string
 * Handles multi-word queries - all query words must match at least one target word
 */
const fuzzyMatch = (target: string, query: string): boolean => {
    if (!query || !target) return false;

    // Exact substring match (fast path)
    if (target.includes(query)) return true;

    // Split both into words
    const targetWords = target.split(/\s+/).filter(w => w.length > 0);
    const queryWords = query.split(/\s+/).filter(w => w.length > 0);

    if (queryWords.length === 0) return false;

    // Every query word must fuzzy match at least one target word
    return queryWords.every(queryWord =>
        targetWords.some(targetWord => fuzzyWordMatch(targetWord, queryWord))
    );
};

export type DetailOption = {
    id: string;
    label: string;
    description?: string;
};

export type SourceItem = {
    id: string;
    label: string;
    name: string;
    description: string;
    details?: {
        title?: string;
        options: DetailOption[];
    };
    enabled?: boolean;
};

export type SourceSelectorValue = {
    selectedSourceId: string | null;
    selectedSourceName: string | null;
    detailSelections: Record<string, string[]>;
};

export interface SourceSelectorProps {
    sources: SourceItem[];
    triggerLabel?: string;
    enableDetailSearch?: boolean;
    defaultSelectedSourceId?: string | null;
    defaultDetailSelections?: Record<string, string[]>;
    onChange?: (value: SourceSelectorValue) => void;
    /** Close dropdown when a source is selected. Default: true */
    closeOnSelect?: boolean;
}

export interface SourceSelectorRef {
    open: () => void;
}

export const SourceSelector = forwardRef<SourceSelectorRef, SourceSelectorProps>(({
    sources,
    triggerLabel,
    enableDetailSearch = true,
    defaultSelectedSourceId = null,
    defaultDetailSelections,
    onChange,
    closeOnSelect = true
}, ref) => {
    const t = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [selectedSourceId, setSelectedSourceId] = useState<string | null>(defaultSelectedSourceId);
    const { handleChangeChosenSource } = useAiWithRag();
    const [activeDetailsSourceId, setActiveDetailsSourceId] = useState<string | null>(null);
    const [detailSearch, setDetailSearch] = useState("");
    const dropdownRef = useRef<HTMLDivElement>(null);
    const isAiPage = window.location.pathname.includes("/ai") || window.location.pathname.includes("/new-ai");

    const effectiveTriggerLabel = triggerLabel ?? t("sourceSelector.title");

    const [detailSelections, setDetailSelections] = useState<Record<string, Set<string>>>(() => {
        if (!defaultDetailSelections) return {};
        return Object.entries(defaultDetailSelections).reduce<Record<string, Set<string>>>(
            (acc, [sourceId, optionIds]) => {
                acc[sourceId] = new Set(optionIds);
                return acc;
            },
            {}
        );
    });

    const open = () => setIsOpen(true);
    const close = () => {
        setIsOpen(false);
        setActiveDetailsSourceId(null);
        setDetailSearch("");
    };

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                close();
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    useImperativeHandle(ref, () => ({
        open
    }));

    const activeSource = useMemo(
        () => sources.find(source => source.id === activeDetailsSourceId) ?? null,
        [activeDetailsSourceId, sources]
    );

    const filteredDetailOptions = useMemo(() => {
        if (!activeSource?.details) return [];
        const options = activeSource.details.options;
        if (!enableDetailSearch || !detailSearch.trim()) return options;
        const query = detailSearch.toLowerCase();
        return options.filter(
            option =>
                fuzzyMatch(option.label.toLowerCase(), query) ||
                fuzzyMatch(option.description?.toLowerCase() ?? "", query)
        );
    }, [activeSource, enableDetailSearch, detailSearch]);

    const toggleDetailSelection = (parentObj: SourceItem, optionId: string) => {
        setSelectedSourceId(JSON.stringify(parentObj));
        setDetailSelections(prev => {
            const next = { ...prev };
            const currentSet = new Set(next[parentObj.id] ?? []);
            if (currentSet.has(optionId)) {
                currentSet.delete(optionId);
            } else {
                currentSet.add(optionId);
            }
            if (currentSet.size === 0) {
                delete next[parentObj.id];
            } else {
                next[parentObj.id] = currentSet;
            }
            const selectedDataArray = Array.from(next[parentObj.id] || []);
            handleChangeChosenSource(parentObj.name as ChosenChatbotSource, selectedDataArray.length > 0 ? selectedDataArray : undefined);
            return next;
        });
    };

    useEffect(() => {
        if (!selectedSourceId) {
            setSelectedSourceId(defaultSelectedSourceId);
        }
    }, [defaultSelectedSourceId])

    useEffect(() => {
        if (!onChange) return;
        const parsedSource = selectedSourceId ? JSON.parse(selectedSourceId) : null;
        const payload: SourceSelectorValue = {
            selectedSourceId: parsedSource?.id || null,
            selectedSourceName: parsedSource?.name || null,
            detailSelections: Object.entries(detailSelections).reduce<Record<string, string[]>>(
                (acc, [sourceId, selections]) => {
                    if (selections.size) {
                        acc[sourceId] = Array.from(selections);
                    }
                    return acc;
                },
                {}
            )
        };
        onChange(payload);
        handleChangeChosenSource(payload.selectedSourceName as ChosenChatbotSource);
    }, [selectedSourceId, detailSelections]);

    return (
        <SimpleOutsideClick onClickOutside={close}>
            <div className="source-selector" ref={dropdownRef}>
                <button
                    type="button"
                    className={`source-selector__trigger ${isOpen ? "source-selector__trigger--active" : ""}`}
                    onClick={() => setIsOpen(!isOpen)}
                    aria-label={effectiveTriggerLabel}
                >
                    <Icon name="source" style={isOpen || (selectedSourceId && JSON.parse(selectedSourceId).id !== "all") ? { fill: "#ea1b0a" } : {}} />
                    <EonUiTooltip slot="tooltip" size="small" placement="top">
                        <div style={{ fontSize: "12px", lineHeight: "1.6" }}>
                            {effectiveTriggerLabel}
                        </div>
                    </EonUiTooltip>
                </button>

                {isOpen && (
                    <div className={`source-selector__dropdown source-selector__dropdown${isAiPage ? "--bottom" : "--top"}`}>
                        <header className="source-selector__dropdown-header">
                            <span className="source-selector__dropdown-title">
                                {!activeSource && t("sourceSelector.title")}
                                {activeSource && (activeSource.details?.title ?? activeSource.label)}
                            </span>
                            <button
                                type="button"
                                className="source-selector__dropdown-close"
                                aria-label="Close"
                                onClick={close}
                            >
                                ×
                            </button>
                        </header>

                        <div className="source-selector__dropdown-content">
                            {!activeSource && (
                                <div className="source-selector__dropdown-options">
                                    <EonUiRadioButtonGroup
                                        scheme="neutral800"
                                        name="source-selector"
                                        onValueChanged={(e) => {
                                            setSelectedSourceId(e.target.value);
                                            if (closeOnSelect) {
                                                close();
                                            }
                                        }}
                                    >
                                        {sources.map(source => {
                                            const hasDetails = !!source.details?.options?.length;
                                            const isSelected = selectedSourceId ? JSON.parse(selectedSourceId).id === source.id : false;
                                            return (
                                                <div key={source.id} className="source-selector__dropdown-item">
                                                    <div className="source-selector__dropdown-item-content">
                                                        <EonUiRadioButton
                                                            initiallyChecked={isSelected}
                                                            value={JSON.stringify(source)}
                                                            label={source.label}
                                                        />
                                                        <p className="source-selector__dropdown-item-desc">
                                                            {source.description}
                                                        </p>
                                                    </div>

                                                    {hasDetails && (
                                                        <button
                                                            type="button"
                                                            className="source-selector__dropdown-expand"
                                                            aria-label="Open details"
                                                            onClick={() => setActiveDetailsSourceId(source.id)}
                                                        >
                                                            ▼
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </EonUiRadioButtonGroup>
                                </div>
                            )}

                            {activeSource && (
                                <>
                                    {enableDetailSearch && (
                                        <div className="source-selector__dropdown-search">
                                            <input
                                                type="search"
                                                placeholder={t("sourceSelector.search")}
                                                value={detailSearch}
                                                onChange={event => setDetailSearch(event.target.value)}
                                            />
                                        </div>
                                    )}

                                    <div className="source-selector__dropdown-options">
                                        {filteredDetailOptions.map(option => {
                                            const checked = !!detailSelections[activeSource.id]?.has(option.id);
                                            return (
                                                <div
                                                    key={option.id}
                                                    className="source-selector__dropdown-option"
                                                >
                                                    <EonUiCheckbox
                                                        label={option.label}
                                                        size="small"
                                                        checked={checked}
                                                        onValueChanged={() =>
                                                            toggleDetailSelection(activeSource, option.id)
                                                        }
                                                    />
                                                    {option.description && (
                                                        <p className="source-selector__dropdown-option-desc">
                                                            {option.description}
                                                        </p>
                                                    )}
                                                </div>
                                            );
                                        })}
                                        {filteredDetailOptions.length === 0 && (
                                            <p className="source-selector__dropdown-empty">{t("sourceSelector.noMatches")}</p>
                                        )}
                                    </div>

                                    <div className="source-selector__dropdown-footer">
                                        <EonUiButton
                                            text={t("sourceSelector.back")}
                                            rank="secondary"
                                            size="small"
                                            onClick={() => setActiveDetailsSourceId(null)}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </SimpleOutsideClick>
    );
});
